import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { getPageSeo } from '../../utils/pageSeo';
import {
  ShoppingBag, Heart, Star, ChevronLeft, ChevronRight,
  Truck, Shield, RotateCcw, MessageCircle, ChevronDown,
  Share2, ZoomIn, Minus, Plus, Tag, MapPin, CheckCircle, Zap, Flame,
  Copy, X, ChevronRight as ChevronRightIcon, Percent, Ticket, ImagePlus, Play
} from 'lucide-react';
import api from '../../utils/api';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import ProductCard from '../../components/common/ProductCard';
import toast from 'react-hot-toast';
import { cldImg } from '../../utils/cloudinary';
import { analyticsViewItem } from '../../utils/analytics';

// ── YouTube Embed ──────────────────────
function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

function isShorts(url) {
  return url && url.includes('/shorts/');
}

function YouTubeEmbed({ url }) {
  const videoId = getYouTubeId(url);
  if (!videoId) return null;
  const shorts = isShorts(url);
  return (
    <div className={`relative w-full bg-black ${shorts ? 'max-w-xs mx-auto' : ''}`}
      style={{ aspectRatio: shorts ? '9/16' : '16/9' }}>
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`}
        title="Product Video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        className="absolute inset-0 w-full h-full"
        style={{ border: 'none' }}
      />
    </div>
  );
}

// ── Image Gallery (Vaaree style — vertical thumbs left) ────────
function ImageGallery({ images, sellingFast, onlyFewLeft, product }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  if (!images?.length) return (
    <div className="aspect-square bg-gray-50 rounded-2xl flex items-center justify-center text-8xl">📦</div>
  );

  const active = images[activeIdx];

  return (
    <>
      <div className="flex gap-3">
        {/* Vertical thumbnails — desktop */}
        {images.length > 1 && (
          <div className="hidden md:flex flex-col gap-2 w-16 flex-shrink-0">
            {images.map((img, i) => (
              <button key={img.id || i} onClick={() => setActiveIdx(i)}
                className={`w-16 h-16 rounded-xl overflow-hidden border-2 flex-shrink-0 transition-all ${i === activeIdx ? 'border-brand-yellow' : 'border-gray-100 hover:border-gray-300'}`}>
                <img src={cldImg(img.image_url, 120)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" draggable="false" onContextMenu={e => e.preventDefault()} />
              </button>
            ))}
          </div>
        )}

        {/* Main image */}
        <div className="flex-1 relative aspect-square rounded-2xl overflow-hidden bg-gray-50 cursor-zoom-in group"
          onClick={() => setZoomed(true)}>
          <img src={cldImg(active.image_url, 800)} alt="Product"
            fetchpriority="high" decoding="async"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            draggable="false" onContextMenu={e => e.preventDefault()} />
          {/* Badges on image */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {product.is_best_seller && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-lg shadow-md" style={{ backgroundColor: '#ff6b35', color: '#fff' }}>Best Seller</span>
            )}
            {product.is_featured && !product.is_best_seller && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-lg shadow-md" style={{ backgroundColor: '#22c55e', color: '#fff' }}>New Arrival</span>
            )}
            {sellingFast && (
              <span className="flex items-center gap-1 bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-lg shadow-md">
                <Zap className="w-3.5 h-3.5 fill-brand-yellow text-brand-yellow" /> Selling Fast
              </span>
            )}
          </div>
          <button className="absolute top-3 right-3 w-9 h-9 bg-white/80 rounded-full flex items-center justify-center shadow">
            <ZoomIn className="w-4 h-4 text-gray-600" />
          </button>
          {images.length > 1 && (
            <>
              <button onClick={e => { e.stopPropagation(); setActiveIdx(i => (i - 1 + images.length) % images.length); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow transition-all opacity-0 group-hover:opacity-100">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={e => { e.stopPropagation(); setActiveIdx(i => (i + 1) % images.length); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow transition-all opacity-0 group-hover:opacity-100">
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Horizontal thumbs — mobile */}
      {images.length > 1 && (
        <div className="flex md:hidden gap-2 overflow-x-auto scrollbar-hide pt-2">
          {images.map((img, i) => (
            <button key={img.id || i} onClick={() => setActiveIdx(i)}
              className={`flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden border-2 transition-all ${i === activeIdx ? 'border-brand-yellow' : 'border-gray-100'}`}>
              <img src={cldImg(img.image_url, 120)} alt="" className="w-full h-full object-cover" draggable="false" onContextMenu={e => e.preventDefault()} />
            </button>
          ))}
        </div>
      )}

      {/* Zoom modal — rendered via portal to avoid stacking context issues */}
      {zoomed && createPortal(
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={() => setZoomed(false)}>
          <img src={cldImg(active.image_url, 1600)} alt="Zoomed" decoding="async" className="max-w-full max-h-full object-contain rounded-xl" draggable="false" onContextMenu={e => e.preventDefault()} />
          <button onClick={e => { e.stopPropagation(); setZoomed(false); }}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white text-xl">✕</button>
        </div>,
        document.body
      )}
    </>
  );
}

// ── Star Rating ────────────────────────────────────────────────
function StarRating({ rating, size = 'sm' }) {
  const s = size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5';
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`${s} ${i <= Math.round(rating) ? 'fill-brand-yellow text-brand-yellow' : 'text-gray-200 fill-gray-200'}`} />
      ))}
    </div>
  );
}

// ── Accordion Section ──────────────────────────────────────────
function Accordion({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-3.5 text-left">
        <span className="text-sm font-semibold text-brand-dark">{title}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="pb-4 text-sm text-gray-500 leading-relaxed">{children}</div>}
    </div>
  );
}

// ── Review Card ────────────────────────────────────────────────
function ReviewCard({ review }) {
  const getImages = () => {
    if (!review.images) return [];
    if (Array.isArray(review.images)) return review.images;
    try {
      return JSON.parse(review.images);
    } catch {
      return [];
    }
  };

  const images = getImages();

  return (
    <div className="bg-gray-50 rounded-2xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-brand-yellow flex items-center justify-center font-bold text-brand-dark text-sm">
            {(review.user?.name || 'A')[0].toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-semibold text-brand-dark">{review.user?.name || 'Anonymous'}</p>
            <p className="text-xs text-gray-400">{new Date(review.createdAt).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</p>
          </div>
        </div>
        <StarRating rating={review.rating} />
      </div>
      {review.title && <p className="text-sm font-semibold text-brand-dark mb-1">{review.title}</p>}
      {review.comment && <p className="text-sm text-gray-500 leading-relaxed mb-3">{review.comment}</p>}
      
      {/* Review Media Gallery */}
      {images.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {images.map((media, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden bg-gray-200 aspect-square">
              {media.type === 'video' ? (
                <>
                  <video 
                    src={media.url} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play className="w-6 h-6 text-white fill-white" />
                  </div>
                </>
              ) : (
                <img
                  src={cldImg(media.url, 400)}
                  alt="Review"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Coupon Popup ───────────────────────────────────────────────
function CouponPopup({ coupons, bestPrice, originalPrice, onClose }) {
  const handleCopy = (code) => {
    navigator.clipboard.writeText(code).then(() => toast.success(`Code "${code}" copied!`, { icon: '📋', duration: 1500 }));
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ zIndex: 9999 }} onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <p className="font-bold text-brand-dark text-base">Best offers for you!</p>
            {bestPrice && (
              <p className="text-teal-600 font-bold text-sm mt-0.5">
                Get it for <span className="text-lg">₹{bestPrice}</span> *
              </p>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors flex-shrink-0">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Coupon list */}
        <div className="px-4 pb-5 space-y-3 max-h-[60vh] overflow-y-auto">
          {coupons.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">No active coupons available right now.</p>
          )}
          {coupons.map(coupon => {
            const isFlat = coupon.type === 'flat';
            const isPct = coupon.type === 'percentage';
            const discountText = isFlat
              ? `Get ₹${Math.round(coupon.value)} Off`
              : isPct
              ? `Get ${Math.round(coupon.value)}% Off`
              : 'Free Shipping';
            const saveText = isFlat
              ? `₹${Math.round(coupon.value)}`
              : isPct
              ? `${Math.round(coupon.value)}%`
              : 'Free';
            const minText = coupon.min_order > 0 ? `on order above ₹${Math.round(coupon.min_order)}` : '';

            return (
              <div key={coupon.id} className="border border-dashed border-gray-200 rounded-xl overflow-hidden">
                {/* Top row */}
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-yellow flex items-center justify-center flex-shrink-0">
                      {isFlat || isPct ? <Percent className="w-4 h-4 text-brand-dark font-bold" /> : <Truck className="w-4 h-4 text-brand-dark" />}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-brand-dark">{discountText}</p>
                      {minText && <p className="text-xs text-gray-400">{minText}</p>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Save</p>
                    <p className="text-sm font-bold text-teal-600">₹{saveText}</p>
                  </div>
                </div>
                {/* Code row */}
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-dashed border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-2">
                    <Ticket className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm font-bold text-brand-dark tracking-widest">Code: {coupon.code}</span>
                  </div>
                  <button onClick={() => handleCopy(coupon.code)}
                    className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-lg transition-colors">
                    <Copy className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            );
          })}
          {bestPrice && (
            <p className="text-xs text-gray-400 text-center pt-1">* Best price shown after applying the highest eligible coupon</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Sticky bottom bar (mobile) ─────────────────────────────────
function StickyBar({ onAdd, onBuyNow, inStock, hasFbtSelected, thumbnail, wishlisted, onWishlist }) {
  return (
    <>
    {/* Mobile: full-width flush bar */}
    <div className="fixed left-0 right-0 z-40 md:hidden bg-white border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.10)]" style={{ bottom: 49 }}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button onClick={onWishlist}
          className="w-9 h-9 flex-shrink-0 border border-gray-200 rounded-xl flex items-center justify-center">
          <Heart className={`w-4 h-4 ${wishlisted ? 'fill-brand-red text-brand-red' : 'text-gray-400'}`} />
        </button>
        {inStock ? (
          <>
            <button onClick={onAdd}
              className="flex-1 h-10 bg-brand-yellow text-brand-dark font-bold rounded-xl text-sm uppercase tracking-wide">
              Add to Cart
            </button>
            <button onClick={onBuyNow}
              className="flex-1 h-10 bg-brand-dark text-white font-bold rounded-xl text-sm uppercase tracking-wide">
              Buy Now
            </button>
          </>
        ) : hasFbtSelected ? (
          <button onClick={onAdd}
            className="flex-1 h-10 bg-brand-yellow text-brand-dark font-bold rounded-xl text-sm uppercase tracking-wide">
            Add to Cart
          </button>
        ) : (
          <div className="flex-1 h-10 bg-gray-100 text-gray-400 font-bold rounded-xl text-sm uppercase tracking-wide flex items-center justify-center">
            Out of Stock
          </div>
        )}
      </div>
    </div>

    {/* Desktop: floating card */}
    <div className="hidden md:flex fixed bottom-4 left-0 right-0 z-40 justify-center px-4 pointer-events-none">
      <div className="flex items-center gap-2 bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.18)] border border-gray-100 px-3 py-2 pointer-events-auto"
        style={{ minWidth: 480, maxWidth: 700 }}>
        {/* Thumbnail */}
        {thumbnail && (
          <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 border border-gray-100">
            <img src={cldImg(thumbnail, 120)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        )}
        {/* Wishlist */}
        <button onClick={onWishlist}
          className="w-9 h-9 flex-shrink-0 border border-gray-200 rounded-xl flex items-center justify-center hover:border-brand-red transition-colors bg-white">
          <Heart className={`w-4 h-4 ${wishlisted ? 'fill-brand-red text-brand-red' : 'text-gray-400'}`} />
        </button>
        {inStock ? (
          <>
            <button onClick={onAdd}
              className="flex-1 h-10 bg-brand-yellow hover:bg-brand-yellow-dark text-brand-dark font-bold rounded-xl text-sm transition-colors uppercase tracking-wide">
              Add to Cart
            </button>
            <button onClick={onBuyNow}
              className="flex-1 h-10 bg-brand-dark hover:bg-gray-800 text-white font-bold rounded-xl text-sm transition-colors uppercase tracking-wide">
              Buy Now
            </button>
          </>
        ) : hasFbtSelected ? (
          <button onClick={onAdd}
            className="flex-1 h-10 bg-brand-yellow hover:bg-brand-yellow-dark text-brand-dark font-bold rounded-xl text-sm transition-colors uppercase tracking-wide">
            Add to Cart
          </button>
        ) : (
          <div className="flex-1 h-10 bg-gray-100 text-gray-400 font-bold rounded-xl text-sm uppercase tracking-wide flex items-center justify-center">
            Out of Stock
          </div>
        )}
      </div>
    </div>
    </>
  );
}

// ── Delivery estimate ──────────────────────────────────────────
function deliveryDate(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}

// ── Main Page ──────────────────────────────────────────────────
export default function ProductDetailPage() {
  const { slug, variantSlug } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { addToCart, setDrawerOpen, activeDeals } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  const [relations, setRelations] = useState(null);
  const [fbtSelected, setFbtSelected] = useState({});
  const [moreProducts, setMoreProducts] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 0, title: '', comment: '', mediaFiles: [] });
  const [reviewMediaPreviews, setReviewMediaPreviews] = useState([]);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [couponPopupOpen, setCouponPopupOpen] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    api.get('/coupons/active').then(({ data }) => setActiveCoupons(data.coupons || [])).catch(() => {});
    api.get('/categories').then(({ data }) => setAllCategories(data.categories || [])).catch(() => {});
    api.get('/products', { params: { limit: 40 } })
      .then(({ data }) => {
        const bs = (data.products || []).filter(p => p.is_best_seller);
        setBestSellers(bs.slice(0, 10));
      }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setRelations(null);
    setMoreProducts([]);
    const apiUrl = variantSlug ? `/products/variant/${variantSlug}` : `/products/${slug}`;
    api.get(apiUrl)
      .then(r => {
        const p = r.data.product;
        setProduct(p);
        // If variant slug — select the active variant
        const activeVariantId = r.data.active_variant_id;
        const activeVariant = activeVariantId
          ? p.variants?.find(v => v.id === activeVariantId) || p.variants?.[0]
          : p.variants?.[0];
        setSelectedVariant(activeVariant || null);
        api.get(`/products/${p.id}/relations`).then(({ data }) => {
          setRelations(data.relations);
          setFbtSelected({});
        }).catch(() => {});
        // Load more from same category
        if (p.category_id) {
          api.get('/products', { params: { category: p.category_id, limit: 8 } })
            .then(({ data }) => setMoreProducts((data.products || []).filter(x => x.id !== p.id).slice(0, 8)))
            .catch(() => {});
        }
        // Track recently viewed (local + server)
        try {
          const key = 'yellove_recently_viewed';
          const prev = JSON.parse(localStorage.getItem(key) || '[]');
          const entry = { 
            slug: p.slug, 
            name: p.name, 
            image: p.images?.[0]?.image_url, 
            price: p.variants?.[0]?.price,
            discount_price: p.variants?.[0]?.discount_price 
          };
          localStorage.setItem(key, JSON.stringify([entry, ...prev.filter(i => i.slug !== p.slug)].slice(0, 10)));
        } catch {}
        api.post(`/products/${p.id}/view`).catch(() => {});
        // Meta ViewContent + GA4 view_item
        {
          const v = activeVariant || p.variants?.[0];
          const price = parseFloat(v?.discount_price) || parseFloat(v?.price) || 0;
          analyticsViewItem({ id: p.id, name: p.name, price });
        }
        try {
          const key = 'yellove_recently_viewed';
          const rv = JSON.parse(localStorage.getItem(key) || '[]');
          setRecentlyViewed(rv.filter(i => i.slug !== p.slug).slice(0, 8));
        } catch {}
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Keep the selected variant in sync with the URL. The load effect above only
  // depends on [slug], and on a /product/variant/:variantSlug route `slug` is
  // undefined — so navigating variant→variant never re-runs it. All variants are
  // already loaded in `product`, so resolve the selection from the URL directly
  // (no refetch, no skeleton). Without this the first variant click didn't apply
  // and only a second click did.
  useEffect(() => {
    if (!product || !variantSlug) return;
    const v = product.variants?.find(x => x.slug === variantSlug);
    if (v && v.id !== selectedVariant?.id) setSelectedVariant(v);
  }, [variantSlug, product]); // eslint-disable-line react-hooks/exhaustive-deps

  // SEO — set page title + meta tags from the product's SEO settings
  useEffect(() => {
    if (!product) return;
    // If admin configured an explicit SEO entry for this exact URL, let RouteSeo handle it.
    if (getPageSeo(pathname)) return;
    const seo = product.seo || {};
    const setMeta = (name, content) => {
      if (!content) return;
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    // Decode any HTML entities (e.g. a stored "&amp;") so meta text reads "&".
    const decode = (s) => { const el = document.createElement('textarea'); el.innerHTML = s || ''; return el.value; };
    const plain = (html) => decode((html || '').replace(/<[^>]*>/g, ' ')).replace(/\s+/g, ' ').trim().slice(0, 160);
    document.title = decode(seo.seo_title || product.name || 'TheYellove');
    setMeta('description', plain(seo.seo_description || product.description));
    setMeta('keywords', decode(seo.seo_keywords || ''));
    return () => { document.title = 'TheYellove'; };
  }, [product, pathname]);

  if (loading) return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-pulse">
        <div className="aspect-square bg-gray-100 rounded-2xl" />
        <div className="space-y-4">
          <div className="h-4 bg-gray-100 rounded w-1/3" />
          <div className="h-6 bg-gray-100 rounded w-3/4" />
          <div className="h-4 bg-gray-100 rounded w-1/2" />
          <div className="h-10 bg-gray-100 rounded w-1/3" />
          <div className="h-32 bg-gray-100 rounded" />
        </div>
      </div>
    </div>
  );

  if (notFound) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
      <p className="text-6xl mb-4">😕</p>
      <h2 className="text-2xl font-black text-brand-dark mb-2">Product Not Found</h2>
      <p className="text-gray-400 mb-6">This product doesn't exist or has been removed.</p>
      <Link to="/products" className="btn-primary rounded-full px-8">Browse Products</Link>
    </div>
  );

  // Check active flash deal — product API or global CartContext deals
  const activeDeal = product.active_deal || activeDeals?.find(d => d.product_id === product.id);
  const dealPrice = activeDeal && parseFloat(activeDeal.deal_price) > 0 ? parseFloat(activeDeal.deal_price) : null;

  const dp = parseFloat(selectedVariant?.discount_price);
  const rp = parseFloat(selectedVariant?.price || product.variants?.[0]?.price || 0);
  const baseEffective = (dp && dp > 0) ? dp : rp;
  const effectivePrice = dealPrice ?? baseEffective;
  const originalPrice = dealPrice ? rp : ((dp && dp > 0) ? rp : null);
  const discount = originalPrice ? Math.round(((originalPrice - effectivePrice) / originalPrice) * 100) : null;
  const inStock = selectedVariant ? selectedVariant.stock > 0 : true;
  const isCustom = product.type === 'custom';

  const handleVariantSelect = (v) => {
    if (!v) return;
    setSelectedVariant(v);
    setQty(1);
    if (v.slug) {
      navigate(`/product/variant/${v.slug}`, { replace: true });
    }
  };
  const colors = [...new Set(product.variants?.map(v => v.color).filter(Boolean))];
  const sizes = [...new Set(product.variants?.filter(v => !selectedVariant?.color || v.color === selectedVariant?.color).map(v => v.size).filter(Boolean))];
  const models = [...new Set(product.variants?.filter(v => (!selectedVariant?.color || v.color === selectedVariant?.color) && (!selectedVariant?.size || v.size === selectedVariant?.size)).map(v => v.model_name).filter(Boolean))];
  const avgRating = product.reviews?.length ? product.reviews.reduce((s, r) => s + r.rating, 0) / product.reviews.length : 0;

  const fbtProducts = relations?.frequently_bought_together || [];
  const fbtTotal = fbtProducts.filter(p => fbtSelected[p.id])
    .reduce((sum, p) => sum + (Number(p.variants?.[0]?.discount_price) || Number(p.variants?.[0]?.price) || 0), effectivePrice);

  const handleAddToCart = () => {
    setAdding(true);
    // Don't add the main product if it's out of stock — only the checked FBT items go in
    if (inStock) addToCart(product, selectedVariant, qty, dealPrice);
    fbtProducts.filter(fp => fbtSelected[fp.id]).forEach(fp => addToCart(fp, fp.variants?.[0], 1));
    setTimeout(() => {
      setAdding(false);
      toast.success('Added to cart!', { icon: '🛒', duration: 2000 });
      if (window.innerWidth >= 768) {
        setDrawerOpen(true);
      }
    }, 300);
  };

  const handleBuyNow = () => {
    if (inStock) addToCart(product, selectedVariant, qty, dealPrice);
    fbtProducts.filter(fp => fbtSelected[fp.id]).forEach(fp => addToCart(fp, fp.variants?.[0], 1));
    navigate('/cart');
  };

  const handleReviewMediaChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length + reviewForm.mediaFiles.length > 5) {
      toast.error('Maximum 5 media files allowed');
      return;
    }

    const newFiles = files.filter(f => {
      const isImage = f.type.startsWith('image/');
      const isVideo = f.type.startsWith('video/');
      return isImage || isVideo;
    });

    if (newFiles.length !== files.length) {
      toast.error('Only images and videos allowed');
    }

    setReviewForm(prev => ({
      ...prev,
      mediaFiles: [...prev.mediaFiles, ...newFiles],
    }));

    // Generate previews
    const newPreviews = newFiles.map((f, idx) => ({
      id: `${Date.now()}-${idx}`,
      name: f.name,
      type: f.type.split('/')[0], // 'image' or 'video'
      url: URL.createObjectURL(f),
      fileIndex: reviewForm.mediaFiles.length + idx,
    }));

    setReviewMediaPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeReviewMedia = (mediaId) => {
    const preview = reviewMediaPreviews.find(m => m.id === mediaId);
    if (!preview) return;

    // Remove from previews
    setReviewMediaPreviews(prev => prev.filter(m => m.id !== mediaId));

    // Remove from files
    setReviewForm(prev => {
      const newFiles = [...prev.mediaFiles];
      newFiles.splice(preview.fileIndex, 1);
      return { ...prev, mediaFiles: newFiles };
    });
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewForm.rating) return toast.error('Please select a star rating');
    
    // Check if user is logged in
    const token = localStorage.getItem('yellove_token');
    if (!token) {
      toast.error('Please log in to submit a review');
      return;
    }

    setReviewSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('product_id', product.id);
      formData.append('rating', reviewForm.rating);
      formData.append('title', reviewForm.title);
      formData.append('comment', reviewForm.comment);

      // Add media files
      reviewForm.mediaFiles.forEach((file, index) => {
        formData.append('review_media', file);
      });

      await api.post('/reviews', formData);
      toast.success('Review submitted! It will appear after approval.');
      setReviewForm({ rating: 0, title: '', comment: '', mediaFiles: [] });
      setReviewMediaPreviews([]);
      setReviewOpen(false);
    } catch (err) {
      console.error('Review submission error:', err.response?.data || err.message);
      toast.error(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const msg = `Hi! I'm interested in: ${product.name} (₹${effectivePrice})\n${window.location.href}`;
    const num = product.whatsapp_number || '917200252350';
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleShare = () => {
    if (navigator.share) navigator.share({ title: product.name, url: window.location.href });
    else { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }
  };

  const handleAddFbt = () => {
    const selected = fbtProducts.filter(p => fbtSelected[p.id]);
    selected.forEach(p => addToCart(p, p.variants?.[0], 1));
    addToCart(product, selectedVariant, 1);
    setTimeout(() => {
      toast.success(`${selected.length + 1} items added to cart!`, { icon: '🛒', duration: 2000 });
      if (window.innerWidth >= 768) {
        setDrawerOpen(true);
      }
    }, 300);
  };

  return (
    <>
      <div className="max-w-6xl mx-auto px-4 py-4 pb-28">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-4 flex-wrap">
          <Link to="/" className="hover:text-brand-dark">Home</Link>
          <span>/</span>
          {product.category && <>
            <Link to={`/category/${product.category.slug}`} className="hover:text-brand-dark">{product.category.name}</Link>
            <span>/</span>
          </>}
          {product.subCategory && <>
            <Link to={`/category/${product.subCategory.slug}`} className="hover:text-brand-dark">{product.subCategory.name}</Link>
            <span>/</span>
          </>}
          <span className="text-brand-dark truncate max-w-xs">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 xl:gap-10">
          {/* ── LEFT: Images ── */}
          <div className="lg:sticky lg:top-20 lg:self-start">
            <ImageGallery
              images={(() => {
                const variantImgs = selectedVariant?.id
                  ? product.images?.filter(img => img.variant_id === selectedVariant.id)
                  : [];
                const parentImgs = product.images?.filter(img => !img.variant_id) || [];
                const firstVariantId = product.variants?.[0]?.id;
                const firstVariantImgs = product.images?.filter(img => img.variant_id === firstVariantId) || [];
                return variantImgs?.length > 0
                  ? variantImgs
                  : parentImgs.length > 0
                    ? parentImgs
                    : firstVariantImgs.length > 0
                      ? firstVariantImgs
                      : product.images || [];
              })()}
              sellingFast={selectedVariant?.show_selling_fast}
              onlyFewLeft={selectedVariant?.show_only_few_left}
              product={product}
            />
          </div>

          {/* ── RIGHT: Info ── */}
          <div className="space-y-4">
            {/* Badges */}
            <div className="flex items-center gap-2 flex-wrap">
              {product.is_today_deal && (
                <span className="bg-brand-red text-white text-xs font-bold px-3 py-1 rounded-full">Today's Deal</span>
              )}
              {dealPrice && (
                <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">⚡ DEAL OF THE DAY</span>
              )}
              {discount && (
                <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">{discount}% OFF</span>
              )}
            </div>

            {/* Title + actions */}
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-lg md:text-xl font-semibold text-brand-dark leading-snug flex-1">
                {selectedVariant?.title || product.name}
              </h1>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => product && toggleWishlist(product.id)}
                  className="w-9 h-9 border border-gray-200 rounded-full flex items-center justify-center hover:border-brand-red transition-colors">
                  <Heart className={`w-4 h-4 ${product && isWishlisted(product.id) ? 'fill-brand-red text-brand-red' : 'text-gray-400'}`} />
                </button>
                <button onClick={handleShare}
                  className="w-9 h-9 border border-gray-200 rounded-full flex items-center justify-center hover:border-gray-400 transition-colors">
                  <Share2 className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Rating */}
            {product.reviews?.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded">
                  <span>{avgRating.toFixed(1)}</span>
                  <Star className="w-3 h-3 fill-white" />
                </div>
                <span className="text-sm text-gray-400">{product.reviews.length} Ratings & Reviews</span>
              </div>
            )}

            {/* Price */}
            <div className="border border-gray-200 rounded-xl px-4 py-3 inline-block">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-2xl font-price font-bold text-brand-dark tracking-tight">₹{effectivePrice}</span>
                {originalPrice && (
                  <>
                    <span className="text-sm text-gray-400 line-through">MRP ₹{originalPrice}</span>
                    <span className="text-xs font-bold text-white bg-teal-600 px-2.5 py-1 rounded-full">{discount}% OFF</span>
                  </>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">Inclusive of all taxes</p>
            </div>

            {/* Offers — Vaaree style coupon button (right below price) */}
            {activeCoupons.length > 0 && (() => {
              const bestFlatCoupon = activeCoupons.find(c => c.type === 'flat' && effectivePrice >= c.min_order);
              const bestPctCoupon = activeCoupons.find(c => c.type === 'percentage' && effectivePrice >= c.min_order);
              let bestSaving = 0;
              if (bestFlatCoupon) bestSaving = Math.max(bestSaving, parseFloat(bestFlatCoupon.value));
              if (bestPctCoupon) bestSaving = Math.max(bestSaving, Math.round(effectivePrice * bestPctCoupon.value / 100));
              const bestPrice = bestSaving > 0 ? Math.round(effectivePrice - bestSaving) : null;
              return (
                <button onClick={() => setCouponPopupOpen(true)}
                  className="w-full flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3 hover:border-brand-yellow hover:bg-brand-yellow/5 transition-all group">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-yellow flex items-center justify-center flex-shrink-0">
                      <Percent className="w-4 h-4 text-brand-dark" />
                    </div>
                    <div className="text-left">
                      {bestPrice ? (
                        <p className="text-sm font-bold text-teal-600">Get it for ₹{bestPrice} <span className="text-xs text-gray-400 font-normal">*</span></p>
                      ) : (
                        <p className="text-sm font-bold text-brand-dark">Best offers for you!</p>
                      )}
                      <p className="text-xs text-gray-400">with {activeCoupons.length} offer{activeCoupons.length > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <ChevronRightIcon className="w-4 h-4 text-gray-400 group-hover:text-brand-dark transition-colors" />
                </button>
              );
            })()}

            {/* Color selector */}
            {colors.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-brand-dark mb-2">
                  Color / Design: <span className="font-normal text-gray-500">{selectedVariant?.color || '—'}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {colors.map(color => {
                    const v = product.variants.find(v => v.color === color);
                    const active = selectedVariant?.color === color;
                    const hex = v?.color_hex;
                    const variantImg = v?.id ? product.images?.find(img => img.variant_id === v.id)?.image_url : null;
                    return (
                      <button key={color} onClick={() => { if (v) handleVariantSelect(v); }}
                        className={`flex flex-col items-center gap-1 p-1.5 rounded-xl border-2 transition-all w-24 ${active ? 'border-brand-yellow bg-brand-yellow/10' : 'border-gray-200 hover:border-gray-300'}`}>
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          {variantImg
                            ? <img src={cldImg(variantImg, 160)} alt={color} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                            : hex
                              ? <div className="w-full h-full" style={{ background: hex }} />
                              : <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">No image</div>
                          }
                        </div>
                        <span className="text-xs font-medium text-center text-brand-dark leading-tight line-clamp-2">{color}</span>
                        <span className="text-[10px] text-gray-400">COLOR</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Size selector */}
            {sizes.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-brand-dark mb-2">
                  Size: <span className="font-normal text-gray-500">{selectedVariant?.size || 'Select a size'}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {sizes.map(size => {
                    const v = product.variants.find(v => v.size === size && (!selectedVariant?.color || v.color === selectedVariant.color));
                    const active = selectedVariant?.size === size;
                    const outOfStock = v && v.stock === 0;
                    return (
                      <button key={size} onClick={() => { if (v && !outOfStock) handleVariantSelect(v); }}
                        disabled={outOfStock}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all relative ${active ? 'border-brand-yellow bg-brand-yellow/10 text-brand-dark' : outOfStock ? 'border-gray-100 text-gray-300 cursor-not-allowed' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                        {size}
                        {outOfStock && <span className="absolute inset-0 flex items-center justify-center"><span className="w-full h-px bg-gray-200 rotate-45 absolute" /></span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Variant Images — fallback only when no structured attribute selectors (color/size/model) exist */}
            {selectedVariant?.id && product.variants && product.variants.length > 1 && colors.length === 0 && sizes.length === 0 && models.length === 0 && (
              <div className="border-2 border-brand-red rounded-xl p-4 bg-white">
                <p className="text-sm font-bold text-brand-dark mb-3">
                  COLOR: <span className="text-brand-red">{selectedVariant?.color || 'Select'}</span>
                </p>
                <div className="flex flex-wrap gap-3">
                  {product.variants.map((variant, idx) => {
                    const variantImg = product.images?.find(img => img.variant_id === variant.id && img.is_thumbnail)
                      || product.images?.find(img => img.variant_id === variant.id);
                    // No image uploaded for this variant → don't render a placeholder tile
                    if (!variantImg) return null;
                    const isSelected = selectedVariant?.id === variant.id;
                    const displayName = [variant.color, variant.size, variant.model_name].filter(Boolean).join(', ');

                    return (
                      <div key={idx} className="flex flex-col items-center gap-2 cursor-pointer" onClick={() => handleVariantSelect(variant)}>
                        <div className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${isSelected ? 'border-brand-yellow shadow-md ring-2 ring-brand-yellow' : 'border-gray-200'}`}>
                          <img src={cldImg(variantImg.image_url, 160)} alt={displayName} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-semibold text-gray-700">{variant.color || variant.size || variant.model_name}</p>
                          <p className="text-xs text-gray-400">COLOR</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Model selector */}
            {models.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-brand-dark mb-2">
                  Model: <span className="font-normal text-gray-500">{selectedVariant?.model_name || 'Select a model'}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {models.map(model => {
                    const v = product.variants.find(v => v.model_name === model && (!selectedVariant?.color || v.color === selectedVariant.color) && (!selectedVariant?.size || v.size === selectedVariant.size));
                    const active = selectedVariant?.model_name === model;
                    const outOfStock = v && v.stock === 0;
                    return (
                      <button key={model} onClick={() => { if (v && !outOfStock) handleVariantSelect(v); }}
                        disabled={outOfStock}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all relative ${active ? 'border-brand-yellow bg-brand-yellow/10 text-brand-dark' : outOfStock ? 'border-gray-100 text-gray-300 cursor-not-allowed' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                        {model}
                        {outOfStock && <span className="absolute inset-0 flex items-center justify-center"><span className="w-full h-px bg-gray-200 rotate-45 absolute" /></span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Stock status */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`w-2 h-2 rounded-full ${inStock ? 'bg-green-500' : 'bg-brand-red'}`} />
              <span className={`text-sm font-medium ${inStock ? 'text-green-600' : 'text-brand-red'}`}>
                {inStock
                  ? selectedVariant?.stock <= 5 ? `Only ${selectedVariant.stock} left!` : 'In Stock'
                  : 'Out of Stock'}
              </span>
              {selectedVariant?.show_only_few_left && inStock && (
                <span className="flex items-center gap-1 bg-red-50 text-brand-red text-xs font-bold px-2.5 py-1 rounded-full">
                  <Flame className="w-3.5 h-3.5" /> Only Few Left
                </span>
              )}
            </div>

            {/* Qty + CTA — desktop (qty selector only; buttons are in sticky bar) */}
            {!isCustom ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-brand-dark">Qty:</span>
                  <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                    <button onClick={() => setQty(q => Math.max(1, q - 1))}
                      className="w-9 h-9 flex items-center justify-center hover:bg-gray-50 transition-colors">
                      <Minus className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={selectedVariant ? selectedVariant.stock : undefined}
                      value={qty}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (isNaN(val)) { setQty(''); return; }
                        const max = selectedVariant ? selectedVariant.stock : Infinity;
                        setQty(Math.max(1, Math.min(val, max)));
                      }}
                      onBlur={() => setQty(q => (!q || q < 1) ? 1 : q)}
                      className="w-12 text-center text-sm font-bold border-0 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      onClick={() => setQty(q => selectedVariant ? Math.min(q + 1, selectedVariant.stock) : q + 1)}
                      disabled={selectedVariant && qty >= selectedVariant.stock}
                      className="w-9 h-9 flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                      <Plus className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button onClick={handleWhatsApp} disabled={!inStock}
                className={`w-full font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors ${
                  inStock
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}>
                <MessageCircle className="w-5 h-5" /> {inStock ? 'Order via WhatsApp' : 'Out of Stock'}
              </button>
            )}

            {/* Delivery estimate */}
            <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-4">
              <MapPin className="w-4 h-4 text-brand-yellow mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-brand-dark">Delivery Estimate</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Chennai: by <span className="font-semibold text-brand-dark">{deliveryDate(4)}</span>
                </p>
                <p className="text-xs text-gray-500">
                  Tamil Nadu: by <span className="font-semibold text-brand-dark">{deliveryDate(6)}</span>
                </p>
                <p className="text-xs text-gray-500">
                  Rest of India: by <span className="font-semibold text-brand-dark">{deliveryDate(9)}</span>
                </p>
              </div>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Truck, title: 'Free Delivery', sub: 'Above ₹499' },
                { icon: Shield, title: '100% Secure', sub: 'Safe payments' },
              ].map(({ icon: Icon, title, sub }) => (
                <div key={title} className="flex flex-col items-center text-center p-3 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 bg-brand-yellow/20 rounded-lg flex items-center justify-center mb-1.5">
                    <Icon className="w-4 h-4 text-brand-dark" />
                  </div>
                  <p className="text-xs font-semibold text-brand-dark">{title}</p>
                  <p className="text-xs text-gray-400">{sub}</p>
                </div>
              ))}
            </div>

            {/* Product Highlights */}
            {product.highlights?.length > 0 && (
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <h3 className="text-sm font-bold text-brand-dark">Product Highlights</h3>
                </div>
                <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-4">
                  {product.highlights.map((h, i) => (
                    <div key={i}>
                      <p className="text-xs text-gray-400 mb-0.5">{h.label}</p>
                      <p className="text-sm font-semibold text-brand-dark">{h.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* YouTube Video */}
            {product.youtube_url && (
              <div className="mb-4 rounded-2xl overflow-hidden border border-gray-100">
                <YouTubeEmbed url={product.youtube_url} />
              </div>
            )}

            {/* Accordion sections */}
            <div className="border-t border-gray-100">
              {(selectedVariant?.description || product.description) && (
                <Accordion title="Product Description" defaultOpen>
                  <div className="prose prose-sm max-w-none text-gray-500"
                    dangerouslySetInnerHTML={{ __html: selectedVariant?.description || product.description }} />
                </Accordion>
              )}

              {/* Tables (size chart, specs etc.) */}
              {product.tables?.map((table, ti) => {
                const cols = Array.isArray(table.columns) ? table.columns : (typeof table.columns === 'string' ? JSON.parse(table.columns) : []);
                const rows = Array.isArray(table.rows) ? table.rows : (typeof table.rows === 'string' ? JSON.parse(table.rows) : []);
                if (!cols.length) return null;
                return (
                  <Accordion key={ti} title={table.title || 'Table'}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr>
                            {cols.map((col, ci) => (
                              <th key={ci} className="text-left px-4 py-2.5 bg-gray-50 text-xs font-bold text-brand-dark border border-gray-100">{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, ri) => (
                            <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                              {cols.map((_, ci) => {
                                if (row[ci] === '__merged__') return null;
                                // calculate rowspan: count consecutive '__merged__' cells below in same column
                                let span = 1;
                                for (let r = ri + 1; r < rows.length; r++) {
                                  if (rows[r][ci] === '__merged__') span++;
                                  else break;
                                }
                                return (
                                  <td key={ci} rowSpan={span > 1 ? span : undefined}
                                    className="px-4 py-2.5 text-gray-600 border border-gray-100 text-xs align-middle">
                                    {row[ci] || '—'}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Accordion>
                );
              })}

              {product.faqs?.length > 0 && (
                <Accordion title="Frequently Asked Questions">
                  <div className="space-y-3">
                    {product.faqs.map((faq, i) => (
                      <div key={i}>
                        <p className="font-semibold text-brand-dark text-xs mb-1">Q: {faq.question}</p>
                        <p className="text-gray-500 text-xs leading-relaxed">A: {faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </Accordion>
              )}

              <Accordion title="Return and Exchange Policy">
                <p>We do not offer returns or exchanges unless the product is received in a damaged condition. To claim a return or exchange, an uninterrupted unboxing video proof (from start to finish) is strictly required. Claims without a continuous, unedited video will not be entertained.</p>
                <p className="mt-2"><strong>Reporting Window:</strong> Any damage must be reported via WhatsApp or email within 2 days of receiving your order.</p>
                <p className="mt-2"><strong>Personalized Items:</strong> Custom or personalized items are completely non-returnable.</p>
              </Accordion>

              {(() => {
                const additionalInfoItems = product.highlights?.filter(h => h.section === 'additional_info') || [];
                const hasStatic = product.weight || product.category || product.tags?.length > 0;
                if (!additionalInfoItems.length && !hasStatic) return null;
                return (
                  <Accordion title="Additional Info">
                    <div className="divide-y divide-gray-100">
                      {additionalInfoItems.map((h, i) => (
                        <div key={i} className="flex py-2.5 gap-4">
                          <span className="text-sm font-semibold text-brand-dark w-40 flex-shrink-0">{h.label}</span>
                          <span className="text-sm text-gray-600">{h.value}</span>
                        </div>
                      ))}
                      {product.weight && (
                        <div className="flex py-2.5 gap-4">
                          <span className="text-sm font-semibold text-brand-dark w-40 flex-shrink-0">Weight</span>
                          <span className="text-sm text-gray-600">{parseFloat(product.weight)}g</span>
                        </div>
                      )}
                      {product.category && (
                        <div className="flex py-2.5 gap-4">
                          <span className="text-sm font-semibold text-brand-dark w-40 flex-shrink-0">Category</span>
                          <span className="text-sm text-gray-600">{product.category.name}</span>
                        </div>
                      )}
                      {product.tags?.length > 0 && (
                        <div className="flex py-2.5 gap-4">
                          <span className="text-sm font-semibold text-brand-dark w-40 flex-shrink-0">Tags</span>
                          <span className="text-sm text-gray-600">{product.tags.map(t => t.tag).join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </Accordion>
                );
              })()}
            </div>

            {/* Frequently Bought Together */}
            {fbtProducts.length > 0 && (
              <div className="border border-gray-100 rounded-2xl p-4 space-y-3">
                <p className="text-sm font-bold text-brand-dark">Frequently Bought Together</p>
                <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                  <Tag className="w-3 h-3" /> <span>Product on this page</span>
                </div>

                {/* Current product */}
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                  <CheckCircle className="w-4 h-4 text-brand-yellow flex-shrink-0" />
                  {product.images?.[0] && <img src={cldImg(product.images[0].image_url, 160)} alt="" loading="lazy" decoding="async" className="w-20 h-20 sm:w-16 sm:h-16 rounded-xl object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-brand-dark truncate">{product.name}</p>
                    <p className="text-sm text-brand-yellow font-bold">₹{effectivePrice}</p>
                  </div>
                </div>

                {fbtProducts.map(fp => {
                  const fpPrice = fp.variants?.[0]?.discount_price || fp.variants?.[0]?.price || 0;
                  const fpThumb = fp.images?.[0]?.image_url;
                  const checked = !!fbtSelected[fp.id];
                  return (
                    <div key={fp.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                      <input type="checkbox" checked={checked}
                        onChange={() => setFbtSelected(prev => ({ ...prev, [fp.id]: !prev[fp.id] }))}
                        className="w-4 h-4 accent-brand-yellow flex-shrink-0 cursor-pointer" />
                      <Link to={`/product/${fp.slug}`} className="flex items-center gap-3 flex-1 min-w-0">
                        {fpThumb ? <img src={cldImg(fpThumb, 160)} alt="" loading="lazy" decoding="async" className="w-20 h-20 sm:w-16 sm:h-16 rounded-xl object-cover flex-shrink-0" /> : <div className="w-20 h-20 sm:w-16 sm:h-16 rounded-xl bg-gray-200 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-brand-dark truncate">{fp.name}</p>
                          <p className="text-sm text-brand-yellow font-bold">₹{fpPrice}</p>
                        </div>
                      </Link>
                    </div>
                  );
                })}

                {Object.values(fbtSelected).some(Boolean) && (
                  <p className="text-xs text-gray-400 pt-1">
                    Selected items will be added when you click <span className="font-semibold text-brand-dark">Add to Cart</span> below.
                  </p>
                )}
              </div>
            )}

            {/* WhatsApp support */}
            <button onClick={handleWhatsApp}
              className="w-full flex items-center justify-center gap-2 py-3 border border-green-200 text-green-600 rounded-xl text-sm font-semibold hover:bg-green-50 transition-colors">
              <MessageCircle className="w-4 h-4" /> Ask us on WhatsApp
            </button>
          </div>
        </div>

        {/* ── Ratings & Reviews ── */}
        <div className="mt-10 border-t border-gray-100 pt-10">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <h2 className="text-xl font-black text-brand-dark">Ratings & Reviews</h2>
            <button onClick={() => setReviewOpen(o => !o)}
              className="px-5 py-2 border-2 border-brand-dark text-brand-dark text-sm font-bold rounded-lg hover:bg-brand-dark hover:text-white transition-colors">
              Write a review
            </button>
          </div>

          {/* Write a review form */}
          {reviewOpen && (
            <form onSubmit={handleReviewSubmit} className="bg-gray-50 rounded-2xl p-5 mb-6 space-y-4">
              <h3 className="font-bold text-brand-dark">Your Review</h3>
              {/* Star picker */}
              <div>
                <p className="text-xs text-gray-500 mb-1">Rating *</p>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(s => (
                    <button key={s} type="button"
                      onMouseEnter={() => setReviewHover(s)}
                      onMouseLeave={() => setReviewHover(0)}
                      onClick={() => setReviewForm(f => ({ ...f, rating: s }))}>
                      <Star className={`w-7 h-7 transition-colors ${s <= (reviewHover || reviewForm.rating) ? 'fill-brand-yellow text-brand-yellow' : 'text-gray-300 fill-gray-100'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Review Title</p>
                <input value={reviewForm.title} onChange={e => setReviewForm(f => ({ ...f, title: e.target.value }))}
                  className="input-field" placeholder="Summarize your experience..." />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Review</p>
                <textarea value={reviewForm.comment} onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                  className="input-field resize-none" rows={4} placeholder="Tell others about your experience..." />
              </div>

              {/* Media Upload */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Add Photos/Videos (Optional)</p>
                <label className="flex items-center justify-center gap-2 w-full border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-brand-yellow hover:bg-brand-yellow/5 transition-all">
                  <ImagePlus className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Click to add up to 5 photos or videos</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleReviewMediaChange}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-400 mt-1">Max 5 files, 10MB each (images & videos)</p>

                {/* Media Previews */}
                {reviewMediaPreviews.length > 0 && (
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {reviewMediaPreviews.map(media => (
                      <div key={media.id} className="relative group rounded-lg overflow-hidden bg-gray-200 aspect-square">
                        {media.type === 'video' ? (
                          <>
                            <video src={media.url} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                              <Play className="w-5 h-5 text-white fill-white" />
                            </div>
                          </>
                        ) : (
                          <img src={media.url} alt="Preview" className="w-full h-full object-cover" />
                        )}
                        <button
                          type="button"
                          onClick={() => removeReviewMedia(media.id)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button type="submit" disabled={reviewSubmitting}
                  className="btn-primary py-2 px-6 text-sm">
                  {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                </button>
                <button type="button" onClick={() => setReviewOpen(false)}
                  className="py-2 px-6 border border-gray-200 rounded-lg text-sm text-gray-500 hover:bg-gray-100">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Reviews list */}
          {product.reviews?.length > 0 ? (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="flex items-center gap-1.5 bg-green-600 text-white text-sm font-bold px-3 py-1.5 rounded-lg">
                  <span>{avgRating.toFixed(1)}</span>
                  <Star className="w-3.5 h-3.5 fill-white" />
                </div>
                <StarRating rating={avgRating} size="md" />
                <span className="text-gray-400 text-sm">{product.reviews.length} {product.reviews.length === 1 ? 'Review' : 'Reviews'}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {product.reviews.map(r => <ReviewCard key={r.id} review={r} />)}
              </div>
            </>
          ) : (
            <div className="text-center py-10">
              <div className="flex justify-center gap-1 mb-3">
                {[1,2,3,4,5].map(s => <Star key={s} className="w-6 h-6 text-gray-200 fill-gray-100" />)}
              </div>
              <p className="text-sm text-gray-400">Be the first to write a review</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Relation Sections ── */}
      {relations && (() => {
        const sections = [
          { key: 'related', title: 'Related Products' },
          { key: 'you_may_also_like', title: 'You May Also Like' },
          { key: 'recommendation', title: 'Recommended For You' },
        ];
        const hasAny = sections.some(s => relations[s.key]?.length > 0);
        if (!hasAny) return null;
        return (
          <div className="max-w-6xl mx-auto px-4 pb-6 space-y-8">
            {sections.map(section => {
              const prods = relations[section.key];
              if (!prods?.length) return null;
              return (
                <div key={section.key}>
                  <h2 className="text-lg font-black text-brand-dark mb-4">{section.title}</h2>
                  <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide">
                    {prods.map(p => (
                      <div key={p.id} className="flex-shrink-0 w-40 sm:w-44">
                        <ProductCard product={p} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ── Recently Viewed ── */}
      {recentlyViewed.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 pb-8">
          <h2 className="text-lg font-black text-brand-dark mb-4">Recently Viewed Products</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {recentlyViewed.map(p => (
              <ProductCard key={p.slug} product={{
                id: p.id,
                slug: p.slug,
                name: p.name,
                images: p.image ? [{ image_url: p.image }] : [],
                variants: [{ price: p.price, discount_price: p.discount_price || null, stock: 99 }],
                category: p.category ? { name: p.category } : null,
              }} />
            ))}
          </div>
        </div>
      )}

      {/* ── Explore More in Categories ── */}
      {allCategories.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 pb-8">
          <h2 className="text-xl font-black text-brand-dark mb-5">Explore More in Categories</h2>
          {/* Mobile: 2-row scroll */}
          <div className="md:hidden overflow-x-auto scrollbar-hide pr-6">
            <div className="grid grid-rows-2 grid-flow-col gap-x-3 gap-y-3 w-max">
              {allCategories.filter(c => c.image).map(cat => (
                <Link key={cat.id} to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-1.5 group w-24">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 border-2 border-brand-yellow/20 group-hover:border-brand-yellow transition-all duration-300">
                    <img src={cldImg(cat.image, 360)} alt={cat.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </div>
                  <span className="text-xs font-semibold text-brand-dark text-center w-full leading-tight">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
          {/* Desktop: single row horizontal scroll */}
          <div className="hidden md:flex gap-6 overflow-x-auto scrollbar-hide pb-2">
            {allCategories.filter(c => c.image).map(cat => (
              <Link key={cat.id} to={`/category/${cat.slug}`}
                className="flex-shrink-0 flex flex-col items-center gap-2 group">
                <div className="w-[180px] h-[180px] rounded-full overflow-hidden bg-gray-100 border-2 border-brand-yellow/20 group-hover:border-brand-yellow group-hover:shadow-md transition-all duration-300 group-hover:scale-105">
                  <img src={cldImg(cat.image, 360)} alt={cat.name} className="w-full h-full object-cover" />
                </div>
                <span className="text-sm font-semibold text-brand-dark text-center w-[180px] leading-tight">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ── More from same category ── */}
      {moreProducts.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-black text-brand-dark">More from {product?.category?.name || 'Category'}</h2>
            {product?.category?.slug && (
              <Link to={`/category/${product.category.slug}`} className="text-sm font-semibold text-brand-yellow hover:underline">View All</Link>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {moreProducts.map(p => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      )}

      {/* ── Best Sellers ── */}
      {bestSellers.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 pb-10">
          <h2 className="text-lg font-black text-brand-dark mb-4">Best Sellers</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {bestSellers.map(p => (
              <ProductCard key={p.id} product={p} badge="best-seller" />
            ))}
          </div>
        </div>
      )}

      {/* Coupon popup */}
      {couponPopupOpen && (() => {
        const bestFlatCoupon = activeCoupons.find(c => c.type === 'flat' && effectivePrice >= c.min_order);
        const bestPctCoupon = activeCoupons.find(c => c.type === 'percentage' && effectivePrice >= c.min_order);
        let bestSaving = 0;
        if (bestFlatCoupon) bestSaving = Math.max(bestSaving, parseFloat(bestFlatCoupon.value));
        if (bestPctCoupon) bestSaving = Math.max(bestSaving, Math.round(effectivePrice * bestPctCoupon.value / 100));
        const bestPrice = bestSaving > 0 ? Math.round(effectivePrice - bestSaving) : null;
        return (
          <CouponPopup
            coupons={activeCoupons}
            bestPrice={bestPrice}
            originalPrice={effectivePrice}
            onClose={() => setCouponPopupOpen(false)}
          />
        );
      })()}

      {/* Sticky bar — mobile */}
      {!isCustom && (
        <StickyBar
          onAdd={handleAddToCart}
          onBuyNow={handleBuyNow}
          inStock={inStock}
          hasFbtSelected={fbtProducts.some(fp => fbtSelected[fp.id])}
          thumbnail={(() => {
            const variantImg = selectedVariant?.id
              ? product.images?.find(img => img.variant_id === selectedVariant.id)
              : null;
            return variantImg?.image_url || product.images?.[0]?.image_url;
          })()}
          wishlisted={product && isWishlisted(product.id)}
          onWishlist={() => product && toggleWishlist(product.id)}
        />
      )}
    </>
  );
}
